// Facturas recibidas por XML (pedido de Rodrigo, 2026-10-19: «tomar los datos de la factura de compra
// y no tener que digitar»). El proveedor manda el XML al correo de intercambio; el sistema lo lee solo
// y la factura queda aquí, en la bandeja. Al abrirla ya están el proveedor, el folio, las líneas, el
// IVA, el ILA y el total tal como vienen en el XML: solo se dice qué producto nuestro es cada línea
// (la segunda vez ya lo sabe) y si entra al stock. También se puede subir el XML a mano.
import { useEffect, useMemo, useRef, useState } from 'react';
import { guardarArchivo, pedirArchivo } from '../api/cliente';
import { Grilla, type Columna } from '../comun/Grilla';
import { PanelError } from '../comun/Estados';
import { decimal, fecha, pesos, rut } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useBodegas } from '../stock/PantallasStock';
import { BuscadorProducto } from '../ventas/BuscadorProducto';
import { DialogoMotivo } from './comun';
import {
  choqueDeUnidad,
  NOMBRE_TIPO_COMPRA,
  useAccionCompras,
  useRecibido,
  useRecibidos,
  type EstadoRecibido,
  type RecibidoListado,
} from './datos';

const NOMBRE_ESTADO: Record<EstadoRecibido, string> = {
  PENDIENTE: 'Por registrar',
  REGISTRADO: 'Registrada',
  DESCARTADO: 'Descartada',
};

const FUENTE: Record<string, string> = {
  HISTORIAL_CODIGO: 'ya comprado con este código',
  HISTORIAL_DESCRIPCION: 'ya comprado con esta descripción',
  CATALOGO: 'código de barras del catálogo',
};

function Verificacion({ firma, timbre }: { firma: boolean; timbre: boolean }) {
  const ok = firma && timbre;
  return (
    <span
      className={ok ? 'tono-bueno' : 'tono-aviso'}
      title={`Firma ${firma ? 'válida' : 'no verificada'} · Timbre ${timbre ? 'válido' : 'no verificado'}`}
    >
      {ok ? '✓ firmado' : '⚠ revisar'}
    </span>
  );
}

/** Lee archivos del disco a base64 (los bytes tal cual: los DTE vienen en ISO-8859-1). */
async function aBase64(archivo: File): Promise<string> {
  const bytes = new Uint8Array(await archivo.arrayBuffer());
  let binario = '';
  for (let i = 0; i < bytes.length; i += 0x8000)
    binario += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binario);
}

// ================================================================ bandeja

export function FacturasRecibidas({ alAbrir }: { alAbrir: (id: string, titulo: string) => void }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const [estado, setEstado] = useState<EstadoRecibido | 'TODOS'>('PENDIENTE');
  const [texto, setTexto] = useState('');
  const q = useRecibidos(empresaId, { estado, texto: texto.trim() || undefined });
  const accion = useAccionCompras(empresaId);
  const archivo = useRef<HTMLInputElement>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const subir = async (lista: FileList | null) => {
    if (!lista?.length) return;
    const resumen: string[] = [];
    for (const f of Array.from(lista)) {
      try {
        const r = (await accion.mutateAsync({
          ruta: '/compras/recibidos',
          cuerpo: { empresaId, nombreArchivo: f.name, contenidoBase64: await aBase64(f) },
        })) as { documentos: Array<{ resultado: string; folio: number }> };
        for (const d of r.documentos)
          resumen.push(
            `${f.name}: folio ${d.folio} ${
              d.resultado === 'INGRESADO'
                ? 'quedó en la bandeja'
                : d.resultado === 'YA_ESTABA'
                  ? 'ya estaba'
                  : d.resultado === 'RECEPTOR_AJENO'
                    ? 'no es para esta empresa'
                    : d.resultado === 'EMITIDO_POR_LA_EMPRESA'
                      ? 'es nuestro (venta)'
                      : 'no es factura ni nota'
            }`,
          );
      } catch (e) {
        resumen.push(`${f.name}: ${(e as Error).message}`);
      }
    }
    setAviso(resumen.join(' · '));
    if (archivo.current) archivo.current.value = '';
  };

  const columnas: Columna<RecibidoListado>[] = [
    { id: 'fecha', titulo: 'Emisión', celda: (r) => fecha(r.fecha_emision), ancho: '7rem' },
    { id: 'prov', titulo: 'Proveedor', celda: (r) => r.razon_social_emisor },
    { id: 'rut', titulo: 'RUT', celda: (r) => rut(r.rut_emisor), ancho: '8rem' },
    {
      id: 'doc',
      titulo: 'Documento',
      celda: (r) => `${NOMBRE_TIPO_COMPRA[r.tipo_sii] ?? r.tipo_sii} ${r.folio}`,
      ancho: '12rem',
    },
    { id: 'lineas', titulo: 'Líneas', celda: (r) => r.lineas, alinear: 'der', ancho: '4rem' },
    {
      id: 'total',
      titulo: 'Total',
      celda: (r) => pesos(r.monto_total),
      alinear: 'der',
      ancho: '8rem',
    },
    {
      id: 'ver',
      titulo: 'XML',
      celda: (r) => <Verificacion firma={r.firma_valida} timbre={r.timbre_valido} />,
      ancho: '6rem',
    },
    {
      id: 'origen',
      titulo: 'Llegó por',
      celda: (r) => (r.origen === 'CORREO' ? 'correo' : 'archivo'),
      ancho: '5rem',
    },
    {
      id: 'estado',
      titulo: 'Estado',
      celda: (r) =>
        r.estado === 'PENDIENTE' && r.recepcion_id
          ? 'en stock, falta factura'
          : NOMBRE_ESTADO[r.estado],
      ancho: '10rem',
    },
  ];
  const conteo = q.data?.conteo ?? {};
  return (
    <div className="pantalla consulta" data-pantalla="recibidos-compra">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado de la factura recibida"
            value={estado}
            onChange={(e) => setEstado(e.target.value as typeof estado)}
          >
            {(['PENDIENTE', 'REGISTRADO', 'DESCARTADO'] as const).map((e) => (
              <option key={e} value={e}>
                {NOMBRE_ESTADO[e]} ({conteo[e] ?? 0})
              </option>
            ))}
            <option value="TODOS">Todas</option>
          </select>
        </label>
        <label className="campo">
          <span className="etiqueta">Buscar</span>
          <input
            aria-label="Buscar proveedor, RUT o folio"
            placeholder="Proveedor, RUT o folio"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <span className="espaciador" />
        {puede('compras.factura.crear') && (
          <>
            <input
              ref={archivo}
              type="file"
              accept=".xml,text/xml,application/xml"
              multiple
              hidden
              aria-label="Archivo XML"
              onChange={(e) => void subir(e.target.files)}
            />
            <button
              type="button"
              onClick={() => archivo.current?.click()}
              disabled={accion.isPending}
            >
              Subir XML…
            </button>
          </>
        )}
      </div>
      <p className="apagado">
        Las facturas llegan solas desde el correo de intercambio (madeci.sii@gmail.com). Ábrelas
        para confirmar los productos y registrarlas: los montos vienen del XML, no se digitan.
      </p>
      {aviso && <p className="tono-aviso">{aviso}</p>}
      {q.isError && <PanelError error={q.error} />}
      <Grilla
        etiqueta="Facturas recibidas"
        columnas={columnas}
        filas={q.data?.filas ?? []}
        clave={(r) => r.id}
        cargando={q.isLoading}
        vacio={estado === 'PENDIENTE' ? 'No hay facturas por registrar' : 'Sin facturas'}
        alAbrir={(r) => alAbrir(r.id, `${r.razon_social_emisor.split(' ')[0]} ${r.folio}`)}
        autofoco
      />
    </div>
  );
}

// ================================================================ registrar una recibida

type Eleccion =
  | {
      tipo: 'PRODUCTO';
      productoId: string;
      presentacionId: string;
      etiqueta: string;
      fuente?: string;
      /** Factor de la presentación elegida (para avisar si no calza con la caja del XML). */
      factor?: number;
    }
  | { tipo: 'FLETE' }
  | { tipo: 'SIN_PRODUCTO' }
  | { tipo: 'PENDIENTE' };

export function RegistroRecibido({
  recibidoId,
  alRegistrado,
  alAbrirFactura,
  alAbrirNota,
}: {
  recibidoId: string;
  alRegistrado: (documentoId: string, titulo: string) => void;
  alAbrirFactura: (documentoId: string, titulo: string) => void;
  /** Abre la NC/ND registrada desde el XML. */
  alAbrirNota?: (documentoId: string, titulo: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const q = useRecibido(empresaId, recibidoId);
  const bodegas = useBodegas(empresaId);
  const accion = useAccionCompras(empresaId);
  const [elecciones, setElecciones] = useState<Record<number, Eleccion>>({});
  // Cómo entra (Rodrigo, 2026-10-24): factura y stock juntos; stock primero con guía y la factura
  // después (cruza sola); o solo registrar la factura (llegará con guía, o es un gasto).
  const [modo, setModo] = useState<'DIRECTA' | 'GUIA' | 'SOLO'>('DIRECTA');
  const directa = modo !== 'SOLO';
  const [pago, setPago] = useState<{
    condicion: 'CONTADO' | 'CREDITO';
    fechaVencimiento: string;
    formaPago: 'TRANSFERENCIA' | 'CHEQUE' | 'EFECTIVO' | 'TARJETA' | 'OTRO';
  } | null>(null);
  const [bodegaId, setBodegaId] = useState('');
  const [buscando, setBuscando] = useState<number | null>(null);
  const [descartando, setDescartando] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);
  const [nota, setNota] = useState<{ id: string; cruzada: boolean; motivo: string | null } | null>(
    null,
  );
  // Bodega cuenta lo que llegó: por línea, lo que llegó y lo rechazado (roto, vencido…) con su motivo.
  const [contando, setContando] = useState(false);
  const [conteo, setConteo] = useState<
    Record<number, { recibida: string; rechazada: string; motivo: string }>
  >({});

  useEffect(() => {
    if (!bodegaId && bodegas.lista[0]) setBodegaId(bodegas.lista[0].id);
  }, [bodegas.lista, bodegaId]);

  // Lo sugerido entra ya elegido (la persona lo puede cambiar). Una línea «FLETE» se propone como tal.
  useEffect(() => {
    const v = q.data;
    if (!v) return;
    setElecciones((prev) => {
      const nuevo: Record<number, Eleccion> = { ...prev };
      for (const l of v.lectura.lineas) {
        if (nuevo[l.numero]) continue;
        const s = v.lineas.find((x) => x.numero === l.numero)?.sugerencia;
        if (s)
          nuevo[l.numero] = {
            tipo: 'PRODUCTO',
            productoId: s.productoId,
            presentacionId: s.presentacionId,
            etiqueta: s.presentacion
              ? `${s.presentacion.sku} · ${s.presentacion.productoNombre} (${s.presentacion.nombre})`
              : (s.sku ?? 'producto'),
            fuente: s.fuente,
            ...(s.presentacion ? { factor: Number(s.presentacion.factorABase) } : {}),
          };
        else if (/\bFLETE\b|DESPACHO|TRANSPORTE/i.test(l.descripcion))
          nuevo[l.numero] = { tipo: 'FLETE' };
        else nuevo[l.numero] = { tipo: 'PENDIENTE' };
      }
      return nuevo;
    });
  }, [q.data]);

  useEffect(() => {
    const g = q.data?.recibido.guia;
    if (!g) return;
    setElecciones((prev) => {
      const nuevo = { ...prev };
      for (const l of g.lineas)
        if ('como' in l) nuevo[l.numero] = { tipo: l.como };
        else if (
          prev[l.numero]?.tipo !== 'PRODUCTO' ||
          (prev[l.numero] as { productoId?: string }).productoId !== l.productoId
        )
          nuevo[l.numero] = {
            tipo: 'PRODUCTO',
            productoId: l.productoId,
            presentacionId: l.presentacionId,
            etiqueta:
              (prev[l.numero] as { etiqueta?: string } | undefined)?.etiqueta ??
              'producto recibido',
          };
      return nuevo;
    });
  }, [q.data?.recibido.guia]);
  useEffect(() => {
    const d = q.data?.lectura;
    if (!d || pago) return;
    const vence = d.fechaVencimiento ?? d.fechaEmision;
    setPago({
      condicion: vence > d.fechaEmision ? 'CREDITO' : 'CONTADO',
      fechaVencimiento: vence,
      formaPago: 'TRANSFERENCIA',
    });
  }, [q.data?.lectura, pago]);

  const v = q.data;
  const lineas = v?.lectura.lineas ?? [];
  const enGuia = !!v?.recibido.recepcionId;
  const pendientes = lineas.filter(
    (l) => (elecciones[l.numero]?.tipo ?? 'PENDIENTE') === 'PENDIENTE',
  );
  const sinProducto = lineas.filter((l) => elecciones[l.numero]?.tipo === 'SIN_PRODUCTO');
  const productos = lineas.filter((l) => elecciones[l.numero]?.tipo === 'PRODUCTO');
  const conStock = directa && !enGuia && productos.length > 0;
  // 2026-10-30: una línea que viene en cajas de 9 no entra como unidades (ni al revés).
  const unidadXmlDe = (numero: number) => v?.lineas.find((x) => x.numero === numero)?.unidadXml;
  const choqueDe = (numero: number) => {
    const e = elecciones[numero];
    return e?.tipo === 'PRODUCTO' ? choqueDeUnidad(unidadXmlDe(numero), e.factor ?? null) : null;
  };
  const conChoque = productos.filter((l) => choqueDe(l.numero) !== null);
  const facturado = (l: { cantidad?: number | string | null }) => String(l.cantidad ?? 1);
  const conteoDe = (l: { numero: number; cantidad?: number | string | null }) =>
    conteo[l.numero] ?? { recibida: facturado(l), rechazada: '0', motivo: '' };
  const faltanteDe = (l: { numero: number; cantidad?: number | string | null }) => {
    const c = conteoDe(l);
    const entra = Math.max(0, Number(c.recibida || 0) - Number(c.rechazada || 0));
    return Math.max(0, Number(facturado(l)) - entra);
  };
  const conteoInvalido =
    conStock &&
    contando &&
    productos.some((l) => {
      const c = conteoDe(l);
      const rec = Number(c.recibida || 0);
      const rech = Number(c.rechazada || 0);
      return (
        !(rec >= 0) ||
        !(rech >= 0) ||
        rech > rec ||
        (faltanteDe(l) > 0 && c.motivo.trim().length < 3)
      );
    });
  const nadaLlego =
    conStock && contando && productos.every((l) => Number(conteoDe(l).recibida || 0) <= 0);
  const puedeRegistrar =
    !conteoInvalido &&
    !nadaLlego &&
    !!v &&
    v.recibido.estado === 'PENDIENTE' &&
    v.recibido.registrable &&
    !!v.proveedor &&
    pendientes.length === 0 &&
    conChoque.length === 0 &&
    productos.length + sinProducto.length > 0 &&
    (!conStock || (!!bodegaId && sinProducto.length === 0));

  const totales = v?.lectura.totales;
  const adicionales = useMemo(() => totales?.impuestos ?? [], [totales]);

  if (q.isLoading) return <p className="apagado">Cargando la factura…</p>;
  if (q.isError) return <PanelError error={q.error} />;
  if (!v) return null;
  const d = v.lectura;
  const r = v.recibido;
  const conRecargo = lineas.some((l) => l.recargo > 0);

  const conteoCuerpo = () =>
    conStock && contando
      ? {
          conteo: productos.map((l) => {
            const c = conteoDe(l);
            return {
              numero: l.numero,
              cantidadRecibida: c.recibida || '0',
              ...(Number(c.rechazada) > 0 ? { cantidadRechazada: c.rechazada } : {}),
              ...(c.motivo.trim() ? { motivo: c.motivo.trim() } : {}),
            };
          }),
        }
      : {};
  const eleccionesCuerpo = () =>
    lineas.map((l) => {
      const e = elecciones[l.numero]!;
      return e.tipo === 'PRODUCTO'
        ? { numero: l.numero, productoId: e.productoId, presentacionId: e.presentacionId }
        : { numero: l.numero, como: e.tipo === 'FLETE' ? 'FLETE' : 'SIN_PRODUCTO' };
    });
  /** 0111: la mercadería entra al stock con guía; la factura queda pendiente aquí mismo. */
  const recibirConGuia = () =>
    accion.mutate(
      {
        ruta: `/compras/recibidos/${recibidoId}/recepcionar`,
        cuerpo: {
          version: r.version,
          ubicacionId: bodegaId,
          lineas: eleccionesCuerpo(),
          ...conteoCuerpo(),
        },
      },
      {
        onSuccess: (x) => {
          const y = x as { numeroRecepcion?: string };
          setResultado(
            `Entró al stock con la guía de recepción${y.numeroRecepcion ? ` N° ${y.numeroRecepcion}` : ''}. Cuando quieras, registra la factura aquí mismo: se cruza sola con la guía.`,
          );
          void q.refetch();
        },
      },
    );

  const registrar = () => {
    accion.mutate(
      {
        ruta: `/compras/recibidos/${recibidoId}/registrar`,
        cuerpo: {
          version: r.version,
          ...(pago ? { pago } : {}),
          ubicacionId: conStock ? bodegaId : null,
          ...(conStock && contando
            ? {
                conteo: productos.map((l) => {
                  const c = conteoDe(l);
                  return {
                    numero: l.numero,
                    cantidadRecibida: c.recibida || '0',
                    ...(Number(c.rechazada) > 0 ? { cantidadRechazada: c.rechazada } : {}),
                    ...(c.motivo.trim() ? { motivo: c.motivo.trim() } : {}),
                  };
                }),
              }
            : {}),
          lineas: lineas.map((l) => {
            const e = elecciones[l.numero]!;
            return e.tipo === 'PRODUCTO'
              ? { numero: l.numero, productoId: e.productoId, presentacionId: e.presentacionId }
              : { numero: l.numero, como: e.tipo === 'FLETE' ? 'FLETE' : 'SIN_PRODUCTO' };
          }),
        },
      },
      {
        onSuccess: (x) => {
          const y = x as {
            documentoId: string;
            aceptada?: boolean;
            reclamadas?: number;
            diferencias?: unknown[];
          };
          setResultado(
            enGuia
              ? y.aceptada
                ? 'Factura registrada y cruzada con su guía: quedó aceptada con su condición de pago.'
                : 'Factura registrada y cruzada con su guía, pero algo no cuadra (ábrela para resolverlo).'
              : conStock
                ? y.aceptada && y.reclamadas
                  ? `Recibida: lo que llegó bueno entró al stock. ${y.reclamadas === 1 ? 'Una línea quedó reclamada' : `${y.reclamadas} líneas quedaron reclamadas`} al proveedor esperando su nota de crédito; ese monto queda retenido del pago.`
                  : y.aceptada
                    ? 'Registrada: la mercadería entró al stock y la factura quedó aceptada.'
                    : 'Registrada y en stock, pero quedó algo por revisar en la factura (ábrela).'
                : 'Registrada (sin mover stock).',
          );
          alRegistrado(y.documentoId, `FCC ${d.folio}`);
        },
      },
    );
  };

  const descargar = async () =>
    guardarArchivo(
      await pedirArchivo('GET', `/compras/recibidos/${recibidoId}/xml`),
      `DTE_${d.emisor.rut}_T${d.tipoDte}_F${d.folio}.xml`,
    );

  return (
    <div className="pantalla documento-compra" data-pantalla="registro-recibido">
      <div className="encabezado-entidad">
        <span className="ruta-migas">Compras › Facturas recibidas (XML)</span>
        <h2>
          {NOMBRE_TIPO_COMPRA[d.tipoDte] ?? `Tipo ${d.tipoDte}`} N° {d.folio} ·{' '}
          {d.emisor.razonSocial}
        </h2>
        <p className="apagado">
          {rut(d.emisor.rut)} · emitida el {fecha(d.fechaEmision)}
          {d.fechaVencimiento ? ` · vence el ${fecha(d.fechaVencimiento)}` : ''} · llegó por{' '}
          {r.origen === 'CORREO'
            ? `correo${r.correoRemitente ? ` (${r.correoRemitente})` : ''}`
            : 'archivo subido'}{' '}
          · <Verificacion firma={d.firma.valida} timbre={d.timbre.valido} />{' '}
          <button type="button" className="enlace" onClick={() => void descargar()}>
            Descargar XML
          </button>
        </p>
      </div>

      {r.estado !== 'PENDIENTE' && (
        <p className={r.estado === 'REGISTRADO' ? 'tono-bueno' : 'tono-aviso'}>
          {r.estado === 'REGISTRADO' ? 'Ya registrada.' : `Descartada: ${r.motivo ?? ''}`}{' '}
          {r.documentoId && (
            <button
              type="button"
              className="enlace"
              onClick={() => alAbrirFactura(r.documentoId!, `FCC ${d.folio}`)}
            >
              Abrir la factura
            </button>
          )}
          {r.estado === 'DESCARTADO' && puede('compras.factura.crear') && (
            <button
              type="button"
              className="enlace"
              onClick={() =>
                accion.mutate({
                  ruta: `/compras/recibidos/${recibidoId}/reabrir`,
                  cuerpo: { version: r.version },
                })
              }
            >
              Volver a la bandeja
            </button>
          )}
        </p>
      )}
      {resultado && <p className="tono-bueno">{resultado}</p>}
      {enGuia && r.estado === 'PENDIENTE' && (
        <p className="aviso-linea tono-info" data-en-guia>
          La mercadería de esta factura <b>ya entró al stock con su guía de recepción</b>. Falta
          registrar la factura: se cruza sola con la guía (precios, descuentos e impuestos del XML)
          y queda con su condición de pago.
        </p>
      )}
      {!r.registrable && r.estado === 'PENDIENTE' && d.tipoDte === 61 && (
        <div className="aviso-linea tono-info">
          <span>
            Es una <b>nota de crédito</b>
            {d.referencias?.[0]?.folio ? ` de la factura N° ${d.referencias[0].folio}` : ''}. Al
            registrarla se cruza sola con lo que esperaba NC de este proveedor (faltantes,
            devoluciones y reclamos) si los montos cuadran, y libera lo retenido del pago.
          </span>
          {puede('compras.nc.crear') && (
            <button
              type="button"
              className="primario"
              disabled={accion.isPending || !v.proveedor}
              onClick={() =>
                accion.mutate(
                  {
                    ruta: `/compras/recibidos/${recibidoId}/registrar-nota`,
                    cuerpo: { version: r.version },
                  },
                  {
                    onSuccess: (x) => {
                      const y = x as { notaId: string; cruzada: boolean; motivo?: string };
                      setNota({ id: y.notaId, cruzada: y.cruzada, motivo: y.motivo ?? null });
                    },
                  },
                )
              }
            >
              Registrar nota de crédito y cruzarla
            </button>
          )}
        </div>
      )}
      {nota && (
        <p className={nota.cruzada ? 'tono-ok' : 'tono-aviso'}>
          {nota.cruzada
            ? 'Nota de crédito registrada y cruzada: cerró lo que esperaba y liberó lo retenido del pago.'
            : `Nota de crédito registrada, pero no se cruzó sola: ${nota.motivo ?? ''}`}{' '}
          {alAbrirNota && (
            <button
              type="button"
              className="enlace"
              onClick={() => alAbrirNota(nota.id, `NCC ${d.folio}`)}
            >
              abrir la NC
            </button>
          )}
        </p>
      )}
      {!r.registrable && r.estado === 'PENDIENTE' && d.tipoDte !== 61 && (
        <p className="tono-aviso">
          Es una nota de débito: regístrala en Compras › Notas de Crédito / Débito (los datos están
          aquí abajo).
        </p>
      )}
      {!v.proveedor && (
        <p className="tono-malo">
          {d.emisor.razonSocial} ({rut(d.emisor.rut)}) no está creado como proveedor: créalo en
          Proveedores y vuelve a abrir esta factura.
        </p>
      )}
      {v.proveedor?.bloqueado && <p className="tono-malo">El proveedor está bloqueado.</p>}
      {v.yaRegistrada && r.estado === 'PENDIENTE' && (
        <p className="tono-aviso">
          Esta factura ya se había registrado a mano.{' '}
          <button
            type="button"
            className="enlace"
            onClick={() =>
              accion.mutate({
                ruta: `/compras/recibidos/${recibidoId}/vincular`,
                cuerpo: { version: r.version },
              })
            }
          >
            Marcarla como registrada
          </button>{' '}
          ·{' '}
          <button
            type="button"
            className="enlace"
            onClick={() => alAbrirFactura(v.yaRegistrada!.id, `FCC ${d.folio}`)}
          >
            Abrir la registrada
          </button>
        </p>
      )}
      {d.lineasConIva && (
        <p className="apagado">
          El proveedor puso los precios con IVA incluido: se registran en neto.
        </p>
      )}
      {conRecargo && (
        <p className="apagado">
          El recargo de cada producto es su flete (costo logístico): entra al costo de ese producto
          y no paga impuesto adicional.
        </p>
      )}

      <table className="grilla compacta" aria-label="Líneas de la factura recibida">
        <thead>
          <tr>
            <th style={{ width: '3rem' }}>N°</th>
            <th style={{ width: '8rem' }}>Código</th>
            <th>Descripción del proveedor</th>
            <th style={{ width: '6rem' }} className="der">
              Cantidad
            </th>
            <th style={{ width: '4rem' }}>Unid.</th>
            <th style={{ width: '7rem' }} className="der">
              Precio
            </th>
            <th style={{ width: '6rem' }} className="der">
              Desc.
            </th>
            {conRecargo && (
              <th style={{ width: '6rem' }} className="der">
                Flete
              </th>
            )}
            <th style={{ width: '7rem' }} className="der">
              Neto
            </th>
            <th style={{ width: '4rem' }}>Imp.</th>
            <th style={{ width: '22rem' }}>Producto nuestro</th>
          </tr>
        </thead>
        <tbody>
          {lineas.map((l) => {
            const e = elecciones[l.numero] ?? { tipo: 'PENDIENTE' };
            const editable = r.estado === 'PENDIENTE';
            return (
              <tr key={l.numero} className={e.tipo === 'PENDIENTE' ? 'fila-aviso' : undefined}>
                <td>{l.numero}</td>
                <td>{l.codigos[0]?.valor ?? '—'}</td>
                <td>
                  {l.descripcion}
                  {l.detalle && !/^[\d|]+$/.test(l.detalle) && (
                    <div className="apagado">{l.detalle}</div>
                  )}
                </td>
                <td className="der">{l.cantidad ? decimal(l.cantidad) : '—'}</td>
                <td>{l.unidad ?? ''}</td>
                <td className="der">{l.precio ? decimal(l.precio) : '—'}</td>
                <td className="der">{l.descuento ? pesos(String(l.descuento)) : ''}</td>
                {conRecargo && <td className="der">{l.recargo ? pesos(String(l.recargo)) : ''}</td>}
                <td className="der">{pesos(String(l.monto))}</td>
                <td>{l.exenta ? 'exento' : (l.codigoImpuesto ?? '')}</td>
                <td>
                  {e.tipo === 'PRODUCTO' && (
                    <span title={e.fuente ? FUENTE[e.fuente] : undefined}>
                      {e.etiqueta}
                      {e.fuente && <span className="apagado"> · {FUENTE[e.fuente]}</span>}
                    </span>
                  )}
                  {choqueDe(l.numero) && (
                    <div className="tono-malo" role="alert">
                      El XML {choqueDe(l.numero)}: elige la presentación de la caja.
                    </div>
                  )}
                  {e.tipo === 'FLETE' && <b>Flete (se reparte al costo)</b>}
                  {e.tipo === 'SIN_PRODUCTO' && (
                    <span className="apagado">Sin producto (gasto/servicio)</span>
                  )}
                  {e.tipo === 'PENDIENTE' && <span className="tono-aviso">¿Qué producto es?</span>}
                  {editable && (
                    <div className="acciones-linea">
                      <button
                        type="button"
                        className="boton-mini"
                        onClick={() => setBuscando(l.numero)}
                      >
                        {e.tipo === 'PRODUCTO' ? 'Cambiar' : 'Elegir producto'}
                      </button>
                      <button
                        type="button"
                        className="boton-mini"
                        onClick={() =>
                          setElecciones((x) => ({ ...x, [l.numero]: { tipo: 'FLETE' } }))
                        }
                      >
                        Flete
                      </button>
                      <button
                        type="button"
                        className="boton-mini"
                        onClick={() =>
                          setElecciones((x) => ({ ...x, [l.numero]: { tipo: 'SIN_PRODUCTO' } }))
                        }
                      >
                        Sin producto
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <dl className="cabecera-od">
        <div>
          <dt>Neto</dt>
          <dd>{pesos(String(d.totales.neto))}</dd>
        </div>
        {d.totales.exento > 0 && (
          <div>
            <dt>Exento</dt>
            <dd>{pesos(String(d.totales.exento))}</dd>
          </div>
        )}
        <div>
          <dt>IVA {d.totales.tasaIva ? `${d.totales.tasaIva}%` : ''}</dt>
          <dd>{pesos(String(d.totales.iva))}</dd>
        </div>
        {adicionales.map((i) => (
          <div key={i.codigo}>
            <dt>
              Imp. {i.codigo}
              {i.tasa ? ` (${i.tasa}%)` : ''}
            </dt>
            <dd>{pesos(String(i.monto))}</dd>
          </div>
        ))}
        <div>
          <dt>Total</dt>
          <dd>
            <b>{pesos(String(d.totales.total))}</b>
          </dd>
        </div>
      </dl>

      {r.estado === 'PENDIENTE' && r.registrable && !enGuia && (
        <section className="paso-cobro entrada-mercaderia" aria-label="Entrada de mercadería">
          <div className="barra-filtros">
            <div className="campo" role="radiogroup" aria-label="¿Cómo llegó la mercadería?">
              <span className="etiqueta">¿Cómo llegó la mercadería?</span>
              <label className="opcion-radio">
                <input
                  type="radio"
                  name="entrada-xml"
                  checked={modo === 'DIRECTA'}
                  onChange={() => setModo('DIRECTA')}
                />
                Llegó con esta factura: <b>entra al stock y se registra la factura</b>
              </label>
              <label className="opcion-radio">
                <input
                  type="radio"
                  name="entrada-xml"
                  checked={modo === 'GUIA'}
                  onChange={() => setModo('GUIA')}
                />
                Llegó: <b>entra al stock con guía de recepción</b>; la factura la registro después
                (descuentos y pago)
              </label>
              <label className="opcion-radio">
                <input
                  type="radio"
                  name="entrada-xml"
                  checked={modo === 'SOLO'}
                  onChange={() => setModo('SOLO')}
                />
                Llegó o llegará con otra guía, o es un gasto: solo registrar la factura
              </label>
            </div>
            {directa && (
              <label className="campo">
                <span className="etiqueta">Bodega donde entra</span>
                <select
                  aria-label="Bodega donde entra"
                  value={bodegaId}
                  onChange={(e) => setBodegaId(e.target.value)}
                >
                  {bodegas.lista.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.nombre}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {directa && productos.length > 0 && (
            <label className="opcion-radio">
              <input
                type="checkbox"
                checked={contando}
                onChange={(e) => setContando(e.target.checked)}
              />
              Algo faltó o llegó roto: <b>contar lo que llegó</b>
            </label>
          )}
          {conStock && contando && (
            <table className="tabla tabla-conteo" aria-label="Conteo de lo que llegó">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Producto</th>
                  <th className="der">Facturado</th>
                  <th className="der">Llegó</th>
                  <th className="der">Roto / rechazado</th>
                  <th className="der">No entra</th>
                  <th>Motivo (faltó, llegó roto…)</th>
                </tr>
              </thead>
              <tbody>
                {productos.map((l) => {
                  const c = conteoDe(l);
                  const falta = faltanteDe(l);
                  const cambiar = (campo: 'recibida' | 'rechazada' | 'motivo', valor: string) =>
                    setConteo((x) => ({ ...x, [l.numero]: { ...conteoDe(l), [campo]: valor } }));
                  const e = elecciones[l.numero];
                  return (
                    <tr key={l.numero}>
                      <td>{l.numero}</td>
                      <td>{e?.tipo === 'PRODUCTO' ? e.etiqueta : l.descripcion}</td>
                      <td className="der">{decimal(facturado(l))}</td>
                      <td className="der">
                        <input
                          aria-label={`Llegó línea ${l.numero}`}
                          inputMode="decimal"
                          className="num"
                          value={c.recibida}
                          onChange={(ev) => cambiar('recibida', ev.target.value.replace(',', '.'))}
                        />
                      </td>
                      <td className="der">
                        <input
                          aria-label={`Rechazado línea ${l.numero}`}
                          inputMode="decimal"
                          className="num"
                          value={c.rechazada}
                          onChange={(ev) => cambiar('rechazada', ev.target.value.replace(',', '.'))}
                        />
                      </td>
                      <td className={`der ${falta > 0 ? 'tono-malo' : ''}`}>
                        {falta > 0 ? decimal(String(falta)) : '—'}
                      </td>
                      <td>
                        {falta > 0 && (
                          <input
                            aria-label={`Motivo línea ${l.numero}`}
                            value={c.motivo}
                            placeholder="Ej.: 2 cajas rotas"
                            onChange={(ev) => cambiar('motivo', ev.target.value)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {conStock && contando && productos.some((l) => faltanteDe(l) > 0) && (
            <p className="apagado">
              Entra al stock solo lo que llegó bueno. Lo que no entra queda{' '}
              <b>reclamado al proveedor esperando su nota de crédito</b>: la factura se acepta igual
              y ese monto queda retenido del pago hasta que llegue la NC.
            </p>
          )}
          {pendientes.length > 0 && (
            <p className="tono-aviso">
              Falta decir qué producto es en {pendientes.length === 1 ? 'la línea' : 'las líneas'}{' '}
              {pendientes.map((l) => l.numero).join(', ')}. La próxima factura de este proveedor ya
              lo sabrá.
            </p>
          )}
          {conStock && sinProducto.length > 0 && (
            <p className="tono-aviso">
              Para entrar al stock, cada línea debe ser un producto o el flete (o elige «solo
              registrar»).
            </p>
          )}
        </section>
      )}

      {r.estado === 'PENDIENTE' && r.registrable && pago && (modo !== 'GUIA' || enGuia) && (
        <fieldset className="recuadro-ficha" aria-label="Condición de pago">
          <legend>Condición de pago de la factura</legend>
          <div className="barra-filtros">
            <label className="campo">
              <span className="etiqueta">Condición</span>
              <select
                aria-label="Condición de pago"
                value={pago.condicion}
                onChange={(e) =>
                  setPago({
                    ...pago,
                    condicion: e.target.value as 'CONTADO' | 'CREDITO',
                    ...(e.target.value === 'CONTADO' ? { fechaVencimiento: d.fechaEmision } : {}),
                  })
                }
              >
                <option value="CONTADO">Contado</option>
                <option value="CREDITO">Crédito</option>
              </select>
            </label>
            <label className="campo">
              <span className="etiqueta">Vence</span>
              <input
                type="date"
                aria-label="Vencimiento"
                min={d.fechaEmision}
                value={pago.fechaVencimiento}
                onChange={(e) => setPago({ ...pago, fechaVencimiento: e.target.value })}
              />
            </label>
            <label className="campo">
              <span className="etiqueta">Forma de pago</span>
              <select
                aria-label="Forma de pago"
                value={pago.formaPago}
                onChange={(e) =>
                  setPago({ ...pago, formaPago: e.target.value as typeof pago.formaPago })
                }
              >
                <option value="TRANSFERENCIA">Transferencia</option>
                <option value="CHEQUE">Cheque</option>
                <option value="EFECTIVO">Efectivo</option>
                <option value="TARJETA">Tarjeta</option>
                <option value="OTRO">Otro</option>
              </select>
            </label>
          </div>
          <p className="apagado">
            {d.fechaVencimiento
              ? `El XML dice que vence el ${fecha(d.fechaVencimiento)}. `
              : 'El XML no trae vencimiento. '}
            Tesorería paga según este vencimiento.
          </p>
        </fieldset>
      )}

      {accion.isError && <PanelError error={accion.error} />}
      {r.estado === 'PENDIENTE' && puede('compras.factura.crear') && (
        <div className="acciones-od">
          {r.registrable && modo === 'GUIA' && !enGuia && (
            <button
              type="button"
              className="primario"
              disabled={!puedeRegistrar || !bodegaId || sinProducto.length > 0 || accion.isPending}
              onClick={recibirConGuia}
            >
              Entrar al stock con guía
            </button>
          )}
          {r.registrable && (modo !== 'GUIA' || enGuia) && (
            <button
              type="button"
              className="primario"
              disabled={!puedeRegistrar || accion.isPending}
              onClick={registrar}
            >
              {enGuia
                ? 'Registrar factura (cruza con la guía)'
                : conStock
                  ? 'Registrar y entrar al stock'
                  : 'Registrar factura'}
            </button>
          )}
          <button type="button" onClick={() => setDescartando(true)}>
            Descartar
          </button>
        </div>
      )}

      {buscando !== null && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={bodegaId || (bodegas.lista[0]?.id ?? '')}
          ubicaciones={bodegas.lista}
          paraCompra
          preferirFactor={unidadXmlDe(buscando)?.pack ?? null}
          alElegir={(p) => {
            setElecciones((x) => ({
              ...x,
              [buscando]: {
                tipo: 'PRODUCTO',
                productoId: p.producto.id,
                presentacionId: p.presentacion.id,
                etiqueta: `${p.producto.sku} · ${p.producto.nombre} (${p.presentacion.nombre})`,
                factor: Number(p.presentacion.factor_a_base),
              },
            }));
            setBuscando(null);
          }}
          alCerrar={() => setBuscando(null)}
        />
      )}
      {descartando && (
        <DialogoMotivo
          titulo={`Descartar ${NOMBRE_TIPO_COMPRA[d.tipoDte] ?? ''} N° ${d.folio}`}
          textoBoton="Descartar"
          explicacion="No se registra: sale de la bandeja (se puede volver a traer)."
          pendiente={accion.isPending}
          error={accion.error}
          alConfirmar={(motivo) =>
            accion.mutate(
              {
                ruta: `/compras/recibidos/${recibidoId}/descartar`,
                cuerpo: { version: r.version, motivo },
              },
              { onSuccess: () => setDescartando(false) },
            )
          }
          alCerrar={() => setDescartando(false)}
        />
      )}
    </div>
  );
}
